import { A11yModule } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, DestroyRef, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { filter } from 'rxjs';

import { AuthService } from '@services/auth/auth.service';
import { Sidebar } from '@shared/components/UI/sidebar/sidebar';
import { ToastContainerComponent } from '@shared/components/UI/Toast/toast-container.component';
import { APP_VERSION } from '@shared/constants/constants';
import { LOGIN_ROUTE } from '@shared/constants/routes.constants';

const COMPACT_LAYOUT_QUERY = '(max-width: 1024px)';
const ADMIN_PAGE_LABELS: Readonly<Record<string, string>> = {
  'productos/detalle': 'Productos',
  'productos/crear': 'Añadir producto',
  'productos/editar': 'Editar producto',
  colores: 'Colores',
  categorias: 'Categorías',
  llaveros: 'Llaveros',
  'cajas-envio': 'Cajas de envío',
  pedidos: 'Pedidos',
  'pagina-principal': 'Página principal',
  'registro-actividad': 'Registro de actividad',
};

@Component({
  selector: 'app-administration',
  standalone: true,
  imports: [A11yModule, RouterOutlet, ToastContainerComponent, Sidebar],
  templateUrl: './administration.html',
  styleUrl: './administration.css',
})
export class Administration implements OnInit, OnDestroy {
  public readonly isMenuOpen = signal(false);
  public readonly pageTitle = signal('Administración');

  public readonly appVersion = APP_VERSION;
  public readonly isCompactLayout = signal(false);

  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public ngOnInit(): void {
    this.updatePageTitle(this.router.url);
    afterNextRender(() => {
      this.document.body.classList.add('admin-layout-active');
      this.breakpointObserver.observe(COMPACT_LAYOUT_QUERY)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({ matches }) => {
          this.closeMenu();
          this.isCompactLayout.set(matches);
        });
    }, { injector: this.injector });
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((event) => {
      this.updatePageTitle(event.urlAfterRedirects);
      this.closeMenu();
    });
  }

  public ngOnDestroy(): void {
    this.document.body.classList.remove('admin-layout-active');
    this.document.body.classList.remove('admin-menu-open');
  }

  public toggleMenu(): void {
    if (this.isMenuOpen()) {
      this.closeMenu();
      return;
    }
    this.isMenuOpen.set(true);
    this.document.body.classList.add('admin-menu-open');
  }

  public closeMenu(): void {
    this.isMenuOpen.set(false);
    this.document.body.classList.remove('admin-menu-open');
  }

  public logout(): void {
    this.closeMenu();
    this.auth.logout().subscribe(() => this.router.navigateByUrl(LOGIN_ROUTE));
  }

  private updatePageTitle(url: string): void {
    const routePath = url.split(/[?#]/)[0].replace(/^\/administracion\/?/, '');
    const matchingRoute = Object.keys(ADMIN_PAGE_LABELS).find(
      (routePrefix) => routePath === routePrefix || routePath.startsWith(`${routePrefix}/`),
    );
    this.pageTitle.set(matchingRoute ? ADMIN_PAGE_LABELS[matchingRoute] : 'Administración');
  }
}
